import { type FC, useEffect, useState } from 'react';
import { Button, Form, Image } from 'react-bootstrap';

import { updatePage } from '@editor2/reducers/editor/editorSlice';
import { pageEditorContainer } from '@editor2/containers/PageElementsPage/styles';
import { type Page } from '@editor2/types/page';
import { pageToPageProperties } from '@editor2/mappers/page';
import CodeMirrorHTML from '@editor2/components/CodeMirrorHTML';
import CopyInput from '@editor2/components/CopyInputWrapper';
import EditMetadataModal from '@editor2/components/EditMetadataModal';
import { useDebouncedDispatch } from '@editor2/hooks/useDebouncedDispatch';

interface Props {
	page: Page;
	chapterId: string;
	courseId: number;
}

const PageEditorForm: FC<Props> = (props) => {
	const { page, chapterId, courseId } = props;
	const initProperties = pageToPageProperties(page);

	const commonPayload = { pageId: page.family_id, courseId };

	const debouncedDispatch = useDebouncedDispatch();

	const [pageProperties, setPageProperties] = useState({ ...initProperties });
	const [isEditMetadataModalShown, setEditMetadataModalShown] = useState(false);

	useEffect(() => {
		const updatedPageProperties = pageToPageProperties(page);

		setPageProperties({
			...pageProperties,
			...updatedPageProperties
		});
	}, [page]);

	const handleChange = (value) => {
		setPageProperties({ ...pageProperties, ...value });
		debouncedDispatch(updatePage({ ...commonPayload, ...value }));
	};

	const handleSplashContentChange = (value: string) => handleChange({ splash_content: value });

	const handlePageMetadataUpdate = (pageMetadata) => handleChange({ metadata: pageMetadata });

	const isUnnumbered = page.page_number === ' ';

	return (
		<Form className={pageEditorContainer}>
			<Form.Group>
				{isUnnumbered && (
					<Form.Check
						type="checkbox"
						checked={true}
						className="pageCheckbox"
						onChange={() => handleChange({ page_number: '', instructor_only: false })}
						label="The page does not take part in renumbering"
					/>
				)}
				{!isUnnumbered && (
					<Form.Control
						size="sm"
						className="pageNumber"
						placeholder="Page number"
						value={pageProperties.page_number}
						onChange={(e) => handleChange({ page_number: e.target.value })}
					/>
				)}

				{isUnnumbered && (
					<Form.Check
						type="checkbox"
						checked={!!pageProperties.instructor_only}
						className="pageCheckbox"
						onChange={() => handleChange({ instructor_only: !pageProperties.instructor_only })}
						label="Instructor-only page"
					/>
				)}
			</Form.Group>

			<Form.Group>
				<Form.Control
					size="lg"
					className="pageName"
					value={pageProperties.name}
					onChange={(e) => handleChange({ name: e.target.value })}
				/>
			</Form.Group>

			<Form.Group>
				<Form.Check
					type="checkbox"
					checked={pageProperties.sub === '1'}
					onChange={(e) => handleChange({ sub: pageProperties.sub === '1' ? '0' : '1' })}
					label="Sub page"
				/>
			</Form.Group>

			<Form.Group>
				<Form.Label>Splash URL</Form.Label>
				<Form.Control
					value={pageProperties.splash_url}
					onChange={(e) => handleChange({ splash_url: e.target.value })}
				/>
			</Form.Group>
			<div className="pageImageContainer">
				{pageProperties.splash_url && (
					<Image src={pageProperties.splash_url} className="pageImage" alt="Page image" thumbnail />
				)}
			</div>

			<Form.Group>
				<Form.Label>Splash content</Form.Label>
				<CodeMirrorHTML
					value={pageProperties.splash_content}
					onChange={handleSplashContentChange}
				/>
			</Form.Group>

			<CopyInput label="Family ID" value={pageProperties.family_id} />

			<CopyInput
				label="Cross-reference Path"
				value={`../../../chapters/${chapterId.replace(
					/-/g,
					''
				)}/pages/${pageProperties.family_id.replace(/-/g, '')}`}
			/>

			<Form.Group>
				<Form.Label>Page group</Form.Label>
				<Form.Control
					value={pageProperties.page_group}
					onChange={(e) => handleChange({ page_group: e.target.value })}
				/>
			</Form.Group>

			<Button variant="outline-primary" size="sm" onClick={() => setEditMetadataModalShown(true)}>
				Edit metadata
			</Button>

			{isEditMetadataModalShown && (
				<EditMetadataModal
					metadata={pageProperties.metadata}
					onHide={() => setEditMetadataModalShown(false)}
					onMetadataUpdate={handlePageMetadataUpdate}
					isShown={isEditMetadataModalShown}
				/>
			)}
		</Form>
	);
};

export default PageEditorForm;
